import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { MarketingLayout } from './marketing-layout';

interface PageData {
  title: string;
  subtitle?: string;
  content: React.ReactNode;
}

function getPages(t: TFunction): Record<string, PageData> {
  return {
    about: {
      title: t('infoPage.aboutTitle', 'Sobre nós'),
      subtitle: t(
        'infoPage.aboutSubtitle',
        'Conectando autores aos melhores profissionais de publicação do mundo.'
      ),
      content: (
        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6 text-muted-foreground">
          <p>
            {t(
              'infoPage.aboutParagraph1',
              'A reformu.lat nasceu da crença de que todo escritor merece acesso a profissionais de alta qualidade — editores, revisores, designers e agentes literários — sem barreiras geográficas ou de orçamento. Nossa plataforma une tecnologia e talento humano para tornar o caminho até a publicação mais direto e transparente.'
            )}
          </p>
          <p>
            {t(
              'infoPage.aboutParagraph2',
              'Somos uma equipe apaixonada por literatura e inovação, distribuída entre São Paulo, Lisboa e Berlim. Desde 2024 já conectamos mais de 3 000 autores a especialistas de conteúdo em 40 países.'
            )}
          </p>
          <p>
            {t('infoPage.aboutParagraph3', 'Nossa missão é simples: dar voz às histórias que o mundo precisa ouvir.')}
          </p>
        </div>
      ),
    },

    blog: {
      title: t('infoPage.blogTitle', 'Blog'),
      subtitle: t('infoPage.blogSubtitle', 'Dicas, tendências e histórias do mundo editorial.'),
      content: (
        <div className="space-y-8 text-muted-foreground">
          <p className="text-sm italic">
            {t(
              'infoPage.blogComingSoon',
              'Em breve — novos artigos toda semana sobre escrita, publicação e carreira literária.'
            )}
          </p>
          {[
            {
              tag: t('infoPage.blogPost1Tag', 'Escrita'),
              title: t('infoPage.blogPost1Title', 'Como criar personagens que o leitor não esquece'),
              date: t('infoPage.blogPost1Date', 'Jun 2026'),
            },
            {
              tag: t('infoPage.blogPost2Tag', 'Publicação'),
              title: t('infoPage.blogPost2Title', 'Self-publishing vs editora tradicional: qual caminho escolher?'),
              date: t('infoPage.blogPost2Date', 'Mai 2026'),
            },
            {
              tag: t('infoPage.blogPost3Tag', 'IA'),
              title: t('infoPage.blogPost3Title', 'Como usar IA para acelerar sua revisão sem perder a voz autoral'),
              date: t('infoPage.blogPost3Date', 'Abr 2026'),
            },
          ].map((post) => (
            <div key={post.title} className="border border-border rounded-xl p-6 hover:border-primary/40 transition-colors">
              <span className="text-xs font-semibold uppercase tracking-wider text-accent">{post.tag}</span>
              <h2 className="mt-2 text-lg font-semibold text-foreground">{post.title}</h2>
              <p className="mt-1 text-xs text-muted-foreground/60">{post.date}</p>
            </div>
          ))}
        </div>
      ),
    },

    careers: {
      title: t('infoPage.careersTitle', 'Carreiras'),
      subtitle: t('infoPage.careersSubtitle', 'Construa o futuro da publicação conosco.'),
      content: (
        <div className="space-y-6 text-muted-foreground">
          <p>
            {t(
              'infoPage.careersParagraph',
              'Acreditamos que grandes produtos nascem de equipes diversas, curiosas e com liberdade para experimentar. Se você quer trabalhar no cruzamento entre literatura, tecnologia e design, este é o seu lugar.'
            )}
          </p>
          <div className="rounded-xl border border-border p-6 space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-accent">
              {t('infoPage.careersOpenPositionsLabel', 'Vagas abertas')}
            </span>
            <p className="text-sm mt-2">
              {t('infoPage.careersNoPositionsBefore', 'Não há vagas abertas no momento. Envie seu currículo para')}{' '}
              <a href="mailto:jobs@reformu.lat" className="text-primary underline underline-offset-4">
                jobs@reformu.lat
              </a>{' '}
              {t(
                'infoPage.careersNoPositionsAfter',
                'e entraremos em contato quando surgir uma oportunidade adequada ao seu perfil.'
              )}
            </p>
          </div>
        </div>
      ),
    },

    contact: {
      title: t('infoPage.contactTitle', 'Contato'),
      subtitle: t('infoPage.contactSubtitle', 'Estamos aqui para ajudar.'),
      content: (
        <div className="space-y-8 text-muted-foreground">
          <p>
            {t(
              'infoPage.contactParagraph',
              'Tem dúvidas, sugestões ou quer saber mais sobre como a reformu.lat pode ajudar no seu projeto? Fale conosco pelos canais abaixo.'
            )}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              { label: t('infoPage.contactLabelSupport', 'Suporte geral'), email: 'suporte@reformu.lat' },
              { label: t('infoPage.contactLabelPartnerships', 'Parcerias'), email: 'parcerias@reformu.lat' },
              { label: t('infoPage.contactLabelPress', 'Imprensa'), email: 'press@reformu.lat' },
              { label: t('infoPage.contactLabelCareers', 'Carreiras'), email: 'jobs@reformu.lat' },
            ].map(({ label, email }) => (
              <div key={email} className="rounded-xl border border-border p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-accent mb-1">{label}</p>
                <a href={`mailto:${email}`} className="text-sm text-primary underline underline-offset-4">
                  {email}
                </a>
              </div>
            ))}
          </div>
          <p className="text-sm">
            {t('infoPage.contactResponseTime', 'Tempo de resposta médio: até 2 dias úteis.')}
          </p>
        </div>
      ),
    },

    privacy: {
      title: t('infoPage.privacyTitle', 'Política de Privacidade'),
      subtitle: t('infoPage.privacySubtitle', 'Como coletamos, usamos e protegemos seus dados.'),
      content: (
        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6 text-muted-foreground">
          <p>
            <strong className="text-foreground">{t('infoPage.privacyLastUpdatedLabel', 'Última atualização:')}</strong>{' '}
            {t('infoPage.privacyLastUpdatedValue', 'junho de 2026.')}
          </p>
          <p>
            {t(
              'infoPage.privacyIntro',
              'A reformu.lat se compromete a proteger a privacidade de seus usuários. Esta política descreve de forma transparente quais dados coletamos, por que os coletamos e como você pode exercer seus direitos.'
            )}
          </p>
          <h2 className="text-foreground text-xl font-semibold mt-8">
            {t('infoPage.privacyDataCollectedHeading', 'Dados que coletamos')}
          </h2>
          <p>
            {t(
              'infoPage.privacyDataCollectedParagraph',
              'Coletamos apenas os dados necessários para fornecer nossos serviços: nome, e-mail, informações de pagamento (processadas por terceiros certificados PCI-DSS) e dados de uso da plataforma para melhorias de produto.'
            )}
          </p>
          <h2 className="text-foreground text-xl font-semibold mt-8">
            {t('infoPage.privacyRightsHeading', 'Seus direitos (LGPD)')}
          </h2>
          <p>
            {t(
              'infoPage.privacyRightsParagraphBefore',
              'Você tem o direito de acessar, corrigir, exportar ou excluir seus dados a qualquer momento. Entre em contato pelo e-mail'
            )}{' '}
            <a href="mailto:privacidade@reformu.lat" className="text-primary underline underline-offset-4">
              privacidade@reformu.lat
            </a>
            .
          </p>
        </div>
      ),
    },

    terms: {
      title: t('infoPage.termsTitle', 'Termos de Serviço'),
      subtitle: t('infoPage.termsSubtitle', 'Regras de uso da plataforma reformu.lat.'),
      content: (
        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6 text-muted-foreground">
          <p>
            <strong className="text-foreground">{t('infoPage.termsLastUpdatedLabel', 'Última atualização:')}</strong>{' '}
            {t('infoPage.termsLastUpdatedValue', 'junho de 2026.')}
          </p>
          <p>
            {t(
              'infoPage.termsIntro',
              'Ao usar a reformu.lat você concorda com estes termos. Leia-os com atenção antes de criar sua conta.'
            )}
          </p>
          <h2 className="text-foreground text-xl font-semibold mt-8">
            {t('infoPage.termsAcceptableUseHeading', 'Uso aceitável')}
          </h2>
          <p>
            {t(
              'infoPage.termsAcceptableUseParagraph',
              'A plataforma deve ser usada exclusivamente para fins lícitos relacionados à produção e publicação editorial. É proibido compartilhar conteúdo ilegal, assediar outros usuários ou contornar sistemas de segurança.'
            )}
          </p>
          <h2 className="text-foreground text-xl font-semibold mt-8">
            {t('infoPage.termsPaymentsHeading', 'Pagamentos e reembolsos')}
          </h2>
          <p>
            {t(
              'infoPage.termsPaymentsParagraph',
              'Todos os pagamentos são processados com segurança. Reembolsos são avaliados em até 7 dias úteis conforme nossa política de cancelamento.'
            )}
          </p>
          <h2 className="text-foreground text-xl font-semibold mt-8">
            {t('infoPage.termsQuestionsHeading', 'Dúvidas')}
          </h2>
          <p>
            {t('infoPage.termsQuestionsParagraphBefore', 'Entre em contato pelo e-mail')}{' '}
            <a href="mailto:legal@reformu.lat" className="text-primary underline underline-offset-4">
              legal@reformu.lat
            </a>
            .
          </p>
        </div>
      ),
    },

    status: {
      title: t('infoPage.statusTitle', 'Status'),
      subtitle: t('infoPage.statusSubtitle', 'Disponibilidade e performance da plataforma em tempo real.'),
      content: (
        <div className="space-y-6 text-muted-foreground">
          <div className="flex items-center gap-3 rounded-xl border border-green-500/30 bg-green-500/5 px-5 py-4">
            <span className="h-2.5 w-2.5 rounded-full bg-green-500 shrink-0" />
            <span className="text-sm font-medium text-foreground">
              {t('infoPage.statusAllOperational', 'Todos os sistemas operacionais')}
            </span>
          </div>
          {[
            { name: t('infoPage.statusItemApi', 'API'), status: t('infoPage.statusOperational', 'Operacional') },
            { name: t('infoPage.statusItemEditor', 'Editor colaborativo'), status: t('infoPage.statusOperational', 'Operacional') },
            { name: t('infoPage.statusItemAuth', 'Autenticação'), status: t('infoPage.statusOperational', 'Operacional') },
            { name: t('infoPage.statusItemExport', 'Exportação de documentos'), status: t('infoPage.statusOperational', 'Operacional') },
            { name: t('infoPage.statusItemPayments', 'Pagamentos'), status: t('infoPage.statusOperational', 'Operacional') },
          ].map(({ name, status }) => (
            <div key={name} className="flex items-center justify-between border-b border-border py-3">
              <span className="text-sm text-foreground">{name}</span>
              <span className="flex items-center gap-2 text-xs text-green-500 font-medium">
                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                {status}
              </span>
            </div>
          ))}
          <p className="text-xs text-muted-foreground/60 pt-2">
            {t('infoPage.statusUpdatedNote', 'Atualizado automaticamente. Histórico de incidentes disponível em breve.')}
          </p>
        </div>
      ),
    },

    'author-trust': {
      title: t('infoPage.authorTrustTitle', 'Confiança do Autor'),
      subtitle: t('infoPage.authorTrustSubtitle', 'Como garantimos segurança e qualidade em cada contratação.'),
      content: (
        <div className="space-y-8 text-muted-foreground">
          <p>
            {t(
              'infoPage.authorTrustIntro',
              'Na reformu.lat, sua segurança é prioridade. Desenvolvemos um sistema robusto de verificação e avaliação para que você contrate profissionais com total confiança.'
            )}
          </p>
          {[
            {
              title: t('infoPage.authorTrustVerifiedTitle', 'Profissionais verificados'),
              desc: t(
                'infoPage.authorTrustVerifiedDesc',
                'Todos os profissionais passam por verificação de identidade e portfólio antes de aparecer na plataforma.'
              ),
            },
            {
              title: t('infoPage.authorTrustEscrowTitle', 'Pagamento seguro em escrow'),
              desc: t(
                'infoPage.authorTrustEscrowDesc',
                'Seu pagamento fica retido até você aprovar a entrega. Sem risco de perder dinheiro.'
              ),
            },
            {
              title: t('infoPage.authorTrustReviewsTitle', 'Avaliações reais'),
              desc: t(
                'infoPage.authorTrustReviewsDesc',
                'Todas as avaliações vêm de projetos concluídos na plataforma — não é possível fabricá-las.'
              ),
            },
            {
              title: t('infoPage.authorTrustSupportTitle', 'Suporte dedicado'),
              desc: t(
                'infoPage.authorTrustSupportDesc',
                'Em caso de disputa, nossa equipe intervém para mediar e garantir um resultado justo.'
              ),
            },
          ].map(({ title, desc }) => (
            <div key={title} className="flex gap-4">
              <span className="mt-1 h-2 w-2 rounded-full bg-accent shrink-0" />
              <div>
                <h3 className="text-base font-semibold text-foreground">{title}</h3>
                <p className="mt-1 text-sm">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      ),
    },

    'join-professional': {
      title: t('infoPage.joinProfessionalTitle', 'Seja um Profissional'),
      subtitle: t(
        'infoPage.joinProfessionalSubtitle',
        'Amplie sua carteira de clientes e cresça na carreira editorial.'
      ),
      content: (
        <div className="space-y-8 text-muted-foreground">
          <p>
            {t(
              'infoPage.joinProfessionalIntro',
              'A reformu.lat é o lugar onde editores, revisores, designers e agentes literários encontram projetos alinhados ao seu nível de expertise — sem prospecção fria e com pagamento garantido.'
            )}
          </p>
          {[
            {
              step: '1',
              title: t('infoPage.joinProfessionalStep1Title', 'Crie seu perfil'),
              desc: t('infoPage.joinProfessionalStep1Desc', 'Mostre seu portfólio, especialidades e disponibilidade em minutos.'),
            },
            {
              step: '2',
              title: t('infoPage.joinProfessionalStep2Title', 'Receba propostas'),
              desc: t('infoPage.joinProfessionalStep2Desc', 'Autores vêm até você com projetos compatíveis com seu perfil.'),
            },
            {
              step: '3',
              title: t('infoPage.joinProfessionalStep3Title', 'Trabalhe e receba'),
              desc: t('infoPage.joinProfessionalStep3Desc', 'Pagamento liberado após aprovação do cliente, sem burocracia.'),
            },
          ].map(({ step, title, desc }) => (
            <div key={step} className="flex gap-5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-bold">
                {step}
              </span>
              <div>
                <h3 className="text-base font-semibold text-foreground">{title}</h3>
                <p className="mt-1 text-sm">{desc}</p>
              </div>
            </div>
          ))}
          <div className="pt-4">
            <a
              href="/app?auth=signup"
              className="inline-block rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              {t('infoPage.joinProfessionalCta', 'Criar perfil profissional')}
            </a>
          </div>
        </div>
      ),
    },

    'freelancer-success': {
      title: t('infoPage.freelancerSuccessTitle', 'Sucesso do Freelancer'),
      subtitle: t(
        'infoPage.freelancerSuccessSubtitle',
        'Recursos e dicas para prosperar como profissional independente.'
      ),
      content: (
        <div className="space-y-8 text-muted-foreground">
          <p>
            {t(
              'infoPage.freelancerSuccessIntro',
              'Ser freelancer no mercado editorial exige mais do que talento — exige gestão, posicionamento e relacionamento. Reunimos aqui o que aprendemos com os profissionais de maior sucesso na plataforma.'
            )}
          </p>
          {[
            {
              title: t('infoPage.freelancerSuccessPortfolioTitle', 'Portfólio que converte'),
              desc: t(
                'infoPage.freelancerSuccessPortfolioDesc',
                'Mostre diversidade de formatos e inclua depoimentos de clientes anteriores.'
              ),
            },
            {
              title: t('infoPage.freelancerSuccessPricingTitle', 'Precificação estratégica'),
              desc: t(
                'infoPage.freelancerSuccessPricingDesc',
                'Comece competitivo, mas suba seus preços à medida que acumula avaliações.'
              ),
            },
            {
              title: t('infoPage.freelancerSuccessCommunicationTitle', 'Comunicação proativa'),
              desc: t(
                'infoPage.freelancerSuccessCommunicationDesc',
                'Clientes valorizam quem atualiza o status do projeto sem ser solicitado.'
              ),
            },
            {
              title: t('infoPage.freelancerSuccessDeadlinesTitle', 'Prazos realistas'),
              desc: t(
                'infoPage.freelancerSuccessDeadlinesDesc',
                'É melhor prometer menos e entregar mais do que o contrário.'
              ),
            },
          ].map(({ title, desc }) => (
            <div key={title} className="rounded-xl border border-border p-5">
              <h3 className="text-base font-semibold text-foreground">{title}</h3>
              <p className="mt-2 text-sm">{desc}</p>
            </div>
          ))}
        </div>
      ),
    },

    community: {
      title: t('infoPage.communityTitle', 'Comunidade'),
      subtitle: t('infoPage.communitySubtitle', 'Conecte-se com autores e profissionais de todo o mundo.'),
      content: (
        <div className="space-y-8 text-muted-foreground">
          <p>
            {t(
              'infoPage.communityIntro',
              'A comunidade reformu.lat reúne escritores, editores, revisores e entusiastas do mundo editorial para trocar experiências, dar feedback e crescer juntos.'
            )}
          </p>
          {[
            {
              platform: 'Discord',
              desc: t(
                'infoPage.communityDiscordDesc',
                'Nosso servidor oficial com canais de feedback, eventos ao vivo e grupos por gênero literário.'
              ),
              href: 'https://discord.com',
            },
            {
              platform: 'Telegram',
              desc: t(
                'infoPage.communityTelegramDesc',
                'Canal de novidades e alertas de oportunidades para profissionais e autores.'
              ),
              href: 'https://telegram.org',
            },
            {
              platform: 'Instagram',
              desc: t(
                'infoPage.communityInstagramDesc',
                'Bastidores, depoimentos e conteúdo sobre escrita e publicação.'
              ),
              href: 'https://instagram.com',
            },
          ].map(({ platform, desc, href }) => (
            <div key={platform} className="rounded-xl border border-border p-5 hover:border-primary/40 transition-colors">
              <h3 className="text-base font-semibold text-foreground">{platform}</h3>
              <p className="mt-1 text-sm">{desc}</p>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-block text-xs font-semibold text-primary underline underline-offset-4"
              >
                {t('infoPage.communityAccessLink', 'Acessar →')}
              </a>
            </div>
          ))}
        </div>
      ),
    },

    services: {
      title: t('infoPage.servicesTitle', 'Serviços & Categorias'),
      subtitle: t(
        'infoPage.servicesSubtitle',
        'Talento especializado para cada etapa da jornada de publicação.'
      ),
      content: (
        <div className="space-y-8 text-muted-foreground">
          <p>
            {t(
              'infoPage.servicesIntro',
              'Do primeiro rascunho ao lançamento, conecte-se com profissionais verificados em todas as áreas que o seu livro precisa.'
            )}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              {
                title: t('infoPage.servicesEditingTitle', 'Edição'),
                desc: t('infoPage.servicesEditingDesc', 'Edição de desenvolvimento, copidesque e revisão final.'),
              },
              {
                title: t('infoPage.servicesDesignTitle', 'Design'),
                desc: t('infoPage.servicesDesignDesc', 'Design de capa, diagramação de miolo e ilustrações.'),
              },
              {
                title: t('infoPage.servicesMarketingTitle', 'Marketing'),
                desc: t('infoPage.servicesMarketingDesc', 'Estratégias de lançamento, anúncios e plataforma de autor.'),
              },
              {
                title: t('infoPage.servicesGhostwritingTitle', 'Ghostwriting'),
                desc: t('infoPage.servicesGhostwritingDesc', 'Escritores profissionais para dar vida às suas ideias.'),
              },
              {
                title: t('infoPage.servicesTranslationTitle', 'Tradução'),
                desc: t('infoPage.servicesTranslationDesc', 'Tradução literária para ampliar o alcance do seu livro.'),
              },
              {
                title: t('infoPage.servicesAudiobookTitle', 'Audiolivro'),
                desc: t('infoPage.servicesAudiobookDesc', 'Narração profissional e produção de audiolivros.'),
              },
            ].map(({ title, desc }) => (
              <div key={title} className="rounded-xl border border-border p-5 hover:border-primary/40 transition-colors">
                <h3 className="text-base font-semibold text-foreground">{title}</h3>
                <p className="mt-2 text-sm">{desc}</p>
              </div>
            ))}
          </div>
          <div className="pt-2">
            <a
              href="/app?auth=signup"
              className="inline-block rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              {t('infoPage.servicesCta', 'Explorar profissionais')}
            </a>
          </div>
        </div>
      ),
    },
  };
}

interface InfoPageProps {
  slug: string;
}

export function InfoPage({ slug }: InfoPageProps) {
  const { t } = useTranslation();
  const PAGES = getPages(t);
  const page = PAGES[slug];

  if (!page) {
    return (
      <MarketingLayout
        title={t('infoPage.notFoundTitle', 'Página não encontrada')}
        subtitle={t('infoPage.notFoundSubtitle', 'O endereço que você acessou não existe.')}
      >
        <p className="text-center text-muted-foreground">
          {t('infoPage.notFoundBeforeLink', 'Volte para a')}{' '}
          <a href="/" className="text-primary underline underline-offset-4">
            {t('infoPage.notFoundHomeLink', 'página inicial')}
          </a>
          .
        </p>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout title={page.title} subtitle={page.subtitle}>
      {page.content}
    </MarketingLayout>
  );
}
